import React from 'react'
import Modal, {
  CloseButton,
  PrevButton
} from '@jarvis.oddle/core/components/Modal'
import { H2, H4, P } from '@jarvis.oddle/core/components/Typo'
import Form from '@jarvis.oddle/core/components/Form'
import {
  getFirstNameError,
  getLastNameError,
  getPasswordError,
  getPhoneError
} from '@jarvis.oddle/core/tools/validation'
import { storeOrderToDeliveryAddress } from '@jarvis.oddle/core/tools/address'
import IconOddlePass from '@jarvis.oddle/core/components/Icons/IconOddlePass'
import { marginSizes } from '@jarvis.oddle/core/theme/default'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import PoweredByOddlePass, {
  UTMSource
} from 'src/components/molecules/PoweredByOddlePass'
import UserContext from 'src/containers/UserContext'
import loginFlow from 'src/flows/loginFlow'
import useTracker from 'src/hooks/useTracker'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectOrder } from 'src/redux/order'
import { selectDetailMenuSite } from 'src/redux/meta'
import useAppDispatch from 'src/hooks/useAppDispatcher'
import { makeControlledModal } from 'src/decorators/ModalController'
import {
  createThenSetUserDefaultAddressAction,
  signUpAction
} from 'src/redux/user'
import { useRouter } from 'next/router'
import InlineTextLink from 'src/components/core/InlineTextLink'

const initialSignUpValues = {
  firstName: '',
  lastName: '',
  telephone: '',
  password: '',
  receiveEmail: true
}

const SignUpModal = ({
  modal,
  isFacebookSignUp,
  prefilledData,
  facebookId,
  hash,
  ocaExist,
  email
}: {
  modal: {
    show: boolean
    onClose: (handler?) => void
    onClosed: () => void
  }
  isFacebookSignUp: boolean
  prefilledData: Record<string, unknown>
  facebookId: string
  hash: string
  email: string
  ocaExist: boolean
}): JSX.Element => {
  const router = useRouter()
  const dispatch = useAppDispatch()
  const { t } = React.useContext(TranslationContext)
  const { storeOrder } = useAppSelector(selectOrder)
  const { menu } = useAppSelector(selectDetailMenuSite) || {}
  const { isOddlePassEnabled } = React.useContext(UserContext)
  const { notifyAccountCompleteRegistration } = useTracker()

  const onSubmit = async (values, { setFieldError, setSubmitting }) => {
    const response = await dispatch(
      signUpAction({
        email,
        facebookId,
        hash,
        phone: values.telephone,
        ...values
      })
    )
    setSubmitting(false)
    if (response.meta.requestStatus === 'fulfilled') {
      notifyAccountCompleteRegistration(response.payload)
      if (storeOrder.data.deliveryAddress) {
        dispatch(
          createThenSetUserDefaultAddressAction(
            storeOrderToDeliveryAddress(storeOrder.data)
          )
        )
      }
      loginFlow.finish()
    } else {
      if (isFacebookSignUp && response.payload.error.includes('email_in_use')) {
        setFieldError('email', t('email_in_use'))
        return
      }
      setFieldError('general', response.payload.error)
    }
  }

  const onValidate = (values) => {
    const { firstName, lastName, telephone, password } = values
    const errors = {} as {
      firstName: string
      lastName: string
      telephone: string
      password: string
    }

    const firstNameError = getFirstNameError(t, firstName)
    if (firstNameError) errors.firstName = firstNameError

    const lastNameError = getLastNameError(t, lastName)
    if (lastNameError) errors.lastName = lastNameError

    const phoneError = getPhoneError(t, telephone)
    if (phoneError) errors.telephone = phoneError

    if (!ocaExist) {
      const passwordError = getPasswordError(t, password)
      if (passwordError) errors.password = passwordError
    }

    return errors
  }

  const initialValues = isFacebookSignUp
    ? prefilledData
    : { ...prefilledData, email }

  const termsAndConditionsLink = <InlineTextLink
    key="termsAndConditions"
    path='/[locale]/terms'
    pathAs={`/${router.query.locale}/terms`}
    text={t('Terms & Conditions')}
    target='_blank'
  />
  const privacyPolicyLink = <InlineTextLink
    key="privacyPolicy"
    path='/[locale]/privacy'
    pathAs={`/${router.query.locale}/privacy`}
    text={t('Privacy Policy')}
    target='_blank'
  />

  return (
    <Modal {...modal} slideInAnimation='top'>
      <Modal.FlexContainer>
        <Modal.Header>
          <PrevButton onClick={() => loginFlow.prev()} />
          {isOddlePassEnabled ? (
            <IconOddlePass />
          ) : (
            <H4 bold normalised>
              {t('One more step...')}
            </H4>
          )}
          <CloseButton onClick={modal.onClose} />
        </Modal.Header>
        <Modal.Body withoutFooter>
          <Modal.Section flexGrow={1}>
            {ocaExist ? (
              <React.Fragment>
                <H2 bold>{`${t('Welcome back, _firstName_!', {
                  firstName: prefilledData.firstName
                })}`}</H2>
                <H4 bold normalised marginBottom={marginSizes.small}>
                  {t(
                    'Verify and share your Oddle Pass details with _menuName_.',
                    { menuName: menu?.name }
                  )}
                </H4>
              </React.Fragment>
            ) : isOddlePassEnabled ? (
              <H4 bold normalised marginBottom={marginSizes.small}>
                {t(
                  'Once successfully created, use Oddle Pass (_email_) across all restaurant partners on Oddle.',
                  {
                    email:
                      email || (prefilledData || {}).email || t('this email')
                  }
                )}
              </H4>
            ) : (
              <H4 bold normalised marginBottom={marginSizes.small}>
                {t('Enter the following details to create your account')}
              </H4>
            )}
            <Form
              initialValues={{ ...initialSignUpValues, ...initialValues }}
              onSubmit={onSubmit}
              onValidate={onValidate}
              validateOnChange={false}
            >
              <Form.ErrorMessage>
                {({ errors }) => (errors.general ? t(errors.general) : null)}
              </Form.ErrorMessage>
              <Form.Input name='firstName' label={t('First Name')} />
              <Form.Input name='lastName' label={t('Last Name')} />
              <Form.Phone
                type='phone'
                name='telephone'
                label={t('Mobile Number')}
                autoComplete='tel-national'
              />
              {(isFacebookSignUp || !ocaExist) && (
                <Form.Input
                  disabled={!isFacebookSignUp}
                  name='email'
                  label={t('Email')}
                />
              )}
              {!ocaExist && (
                <Form.Password name='password' label={t('Your Password')} />
              )}
              <Form.Checkbox name='receiveEmail'>
                {({ checked, disabled }) => (
                  <React.Fragment>
                    <Form.Checkbox.Tick checked={checked} disabled={disabled} />
                    <P bold normalised>
                      {t('Receive promotions from _menuName_', {
                        menuName: menu?.name
                      })}
                    </P>
                  </React.Fragment>
                )}
              </Form.Checkbox>
              <H4
                normalised
                marginTop={marginSizes.medium}
                marginBottom={marginSizes.medium}
              >
                {t(
                  'By signing up, you have read and agreed to our _termsAndConditionsLink_ and _privacyPolicyLink_',
                  {
                    termsAndConditionsLink,
                    privacyPolicyLink
                  }
                )}
              </H4>
              <Form.SubmitButton fullWidth>
                {ocaExist
                  ? t('Continue')
                  : isOddlePassEnabled
                  ? t('Create Oddle Pass')
                  : t('Go')}
              </Form.SubmitButton>
            </Form>
            <PoweredByOddlePass
              UTMSource={UTMSource.SIGNIN_MODAL}
              copyText={
                !ocaExist
                  ? t(
                      'Your _oddlePassLink_ details will be shared with _menuName_',
                      { menuName: menu?.name }
                    )
                  : t(
                      'Oddle Pass can be used to log in to any restaurant partners on Oddle.'
                    )
              }
              linkText={t('Oddle Pass')}
              linkHref={'https://oddle.me/securelogin'}
            />
          </Modal.Section>
        </Modal.Body>
      </Modal.FlexContainer>
    </Modal>
  )
}

export default makeControlledModal(SignUpModal, 'SignUpModal')
